import { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import { useAppSelector } from '@store/hooks'
import {
  getAllDiscount,
  addDiscount,
  updateDiscount,
  removeDiscount,
} from '@store/actions/discount.action'
import { EditingDiscountEntry, DisplayDiscount } from '../billingSettings.types'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { GlobalLoadingActions } from '@context/GlobalLoadingContext'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'

interface UseDiscountsReturn {
  discounts: DisplayDiscount[]
  editingEntries: Map<string, EditingDiscountEntry>
  newEntry: EditingDiscountEntry | null
  handleAddNew: () => void
  handleChange: (
    id: string,
    field: 'status' | 'description' | 'discount_percentage',
    value: string | number,
  ) => void
  handleSave: (id: string) => Promise<void>
  handleEdit: (id: string) => void
  handleCancel: (id: string) => void
  handleDelete: (id: string) => Promise<void>
  handleToggleStatus: (id: string, checked: boolean) => Promise<void>
  getDisplayData: (
    searchQuery: string,
    filters: Record<string, string[]>,
  ) => DisplayDiscount[]
  reset: () => void
}

export const useDiscounts = (
  showToast?: (notification: NotificationToastData) => void,
  showLoading?: GlobalLoadingActions['showLoading'],
  hideLoading?: GlobalLoadingActions['hideLoading'],
  getListFilters?: () => Parameters<typeof getAllDiscount>[0],
): UseDiscountsReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const { discounts } = useSelector((state: RootState) => state.discount)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicId = useMemo(
    () => selectedClinic?.clinic_id || localStorage.getItem('clinic_id'),
    [selectedClinic?.clinic_id],
  )

  const [editingEntries, setEditingEntries] = useState<
    Map<string, EditingDiscountEntry>
  >(new Map())
  const [newEntry, setNewEntry] = useState<EditingDiscountEntry | null>(null)

  const handleAddNew = useCallback((): void => {
    setNewEntry({
      discount_id: '',
      status: SettingStatus.Enabled,
      description: '',
      discount_percentage: 0,
    })
  }, [])

  const handleChange = useCallback(
    (
      id: string,
      field: 'status' | 'description' | 'discount_percentage',
      value: string | number,
    ): void => {
      if (id === '') {
        setNewEntry((prev) =>
          prev
            ? {
                ...prev,
                [field]:
                  field === 'discount_percentage'
                    ? Number(value)
                    : (value as string),
              }
            : null,
        )
      } else {
        setEditingEntries((prev) => {
          const updated = new Map(prev)
          const existingEntry = discounts.find((d) => d.discount_id === id)
          const current = updated.get(id) || {
            discount_id: id,
            status: existingEntry?.status || SettingStatus.Enabled,
            description: existingEntry?.description || '',
            discount_percentage: existingEntry?.discount_percentage || 0,
          }
          updated.set(id, {
            ...current,
            [field]:
              field === 'discount_percentage'
                ? Number(value)
                : (value as string),
          })
          return updated
        })
      }
    },
    [discounts],
  )

  const handleSave = useCallback(
    async (id: string): Promise<void> => {
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        if (id === '') {
          if (!newEntry) return
          if (!clinicId) return
          await dispatch(
            addDiscount({
              discount_id: '',
              status: newEntry.status,
              description: newEntry.description,
              discount_percentage: newEntry.discount_percentage,
              clinic_id: clinicId,
            }),
          ).unwrap()
          setNewEntry(null)
          showToast?.(
            createSuccessToast(
              'Discount saved successfully',
              'Discount has been saved successfully.',
            ),
          )
        } else {
          const editingEntry = editingEntries.get(id)
          if (!editingEntry) return

          const existingEntry = discounts.find((d) => d.discount_id === id)
          if (!existingEntry) {
            if (showToast) {
              showToast(
                createFailedToast(
                  'Discount not found',
                  'Cannot update: entry not found in backend data.',
                ),
              )
            }
            setEditingEntries((prev) => {
              const updated = new Map(prev)
              updated.delete(id)
              return updated
            })
            return
          }

          await dispatch(
            updateDiscount({
              id: existingEntry.discount_id,
              body: {
                status: editingEntry.status,
                description: editingEntry.description,
                discount_percentage: editingEntry.discount_percentage,
                clinic_id: existingEntry.clinic_id,
              },
            }),
          ).unwrap()
          setEditingEntries((prev) => {
            const updated = new Map(prev)
            updated.delete(id)
            return updated
          })
          showToast?.(
            createSuccessToast(
              'Discount updated successfully',
              'Discount has been updated successfully.',
            ),
          )
        }

        await dispatch(getAllDiscount(getListFilters?.()))
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Discount not saved',
            'Discount not saved. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [
      dispatch,
      newEntry,
      editingEntries,
      discounts,
      clinicId,
      getListFilters,
      showToast,
      showLoading,
      hideLoading,
    ],
  )

  const handleEdit = useCallback(
    (id: string): void => {
      const entry = discounts.find((d) => d.discount_id === id)
      if (entry) {
        setEditingEntries((prev) => {
          const updated = new Map(prev)
          updated.set(id, {
            discount_id: entry.discount_id,
            status: entry.status,
            description: entry.description,
            discount_percentage: entry.discount_percentage,
          })
          return updated
        })
      }
    },
    [discounts],
  )

  const handleCancel = useCallback((id: string): void => {
    if (id === '') {
      setNewEntry(null)
    } else {
      setEditingEntries((prev) => {
        const updated = new Map(prev)
        updated.delete(id)
        return updated
      })
    }
  }, [])

  const handleDelete = useCallback(
    async (id: string): Promise<void> => {
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        await dispatch(removeDiscount(id)).unwrap()
        await dispatch(getAllDiscount(getListFilters?.()))
        showToast?.(
          createSuccessToast(
            'Discount deleted successfully',
            'Discount has been deleted successfully.',
          ),
        )
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Discount not deleted',
            'Discount not deleted. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [dispatch, getListFilters, showToast, showLoading, hideLoading],
  )

  const handleToggleStatus = useCallback(
    async (id: string, checked: boolean): Promise<void> => {
      if (!id) return
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        const existingEntry = discounts.find((d) => d.discount_id === id)
        if (!existingEntry) {
          if (showToast) {
            showToast(
              createFailedToast(
                'Discount not found',
                'Cannot update: entry not found in backend data.',
              ),
            )
          }
          return
        }

        const newStatus = checked
          ? SettingStatus.Enabled
          : SettingStatus.Disabled
        await dispatch(
          updateDiscount({
            id: existingEntry.discount_id,
            body: {
              status: newStatus,
              description: existingEntry.description,
              discount_percentage: existingEntry.discount_percentage,
              clinic_id: existingEntry.clinic_id,
            },
          }),
        ).unwrap()
        await dispatch(getAllDiscount(getListFilters?.()))
        showToast?.(
          createSuccessToast(
            'Discount status updated successfully',
            'Discount status has been updated successfully.',
          ),
        )
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Discount status not updated',
            'Discount status not updated. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [dispatch, discounts, getListFilters, showToast, showLoading, hideLoading],
  )

  const getDisplayData = useCallback(
    (
      searchQuery: string,
      filters: Record<string, string[]>,
    ): DisplayDiscount[] => {
      let filtered = [...discounts]

      // Apply search filter
      if (searchQuery) {
        filtered = filtered.filter((item) =>
          item.description.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      }

      // Apply status filter
      if (filters.status && filters.status.length > 0) {
        filtered = filtered.filter((item) =>
          filters.status.includes(item.status),
        )
      }

      const result: DisplayDiscount[] = []

      // Add new entry if it exists
      if (newEntry && clinicId) {
        result.push({
          discount_id: '',
          status: newEntry.status,
          description: newEntry.description,
          discount_percentage: newEntry.discount_percentage,
          clinic_id: clinicId,
          isEditing: true,
        })
      }

      // Add all discount settings with editing state
      filtered.forEach((entry) => {
        const editingEntry = editingEntries.get(entry.discount_id)
        result.push({
          ...entry,
          ...(editingEntry && {
            status: editingEntry.status,
            description: editingEntry.description,
            discount_percentage: editingEntry.discount_percentage,
          }),
          isEditing: editingEntries.has(entry.discount_id),
        })
      })

      return result
    },
    [discounts, newEntry, editingEntries, clinicId],
  )

  const reset = useCallback((): void => {
    setEditingEntries(new Map())
    setNewEntry(null)
  }, [])

  // Memoize the return object to prevent unnecessary re-renders
  // Note: discounts is not directly used - components use getDisplayData() instead
  return useMemo(
    () => ({
      discounts: [] as DisplayDiscount[],
      editingEntries,
      newEntry,
      handleAddNew,
      handleChange,
      handleSave,
      handleEdit,
      handleCancel,
      handleDelete,
      handleToggleStatus,
      getDisplayData,
      reset,
    }),
    [
      editingEntries,
      newEntry,
      handleAddNew,
      handleChange,
      handleSave,
      handleEdit,
      handleCancel,
      handleDelete,
      handleToggleStatus,
      getDisplayData,
      reset,
    ],
  )
}
